<template>
  <Teleport to="body">
    <div
      v-if="visible"
      ref="wrapper"
      class="milestone-route-transition"
      :style="bounds"
      aria-hidden="true"
    >
      <MilestonePageSkeleton
        id="milestonesTransition"
        ref="skeleton"
        idLeft="milestonesTransitionLeft"
        idRight="milestonesTransitionRight"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/** Reuses the item animation only while moving between Roadmap and a Milestone. */
import { useRouter } from "vue-router"
import { nextTick, onBeforeUnmount, ref, watch } from "vue"

import MilestonePageSkeleton from "@/components/Milestone/MilestonePageSkeleton.vue"

import { useWait } from "@/plugins/vuewait"
import { storyOpeningAnimation, storyScalingAnimation } from "@/utils/gsapAnimation"

import type { ComponentPublicInstance, CSSProperties, WatchStopHandle } from "vue"
import type { RouteLocationNormalized } from "vue-router"

const router = useRouter()
const wait = useWait()
const visible = ref(false)
const bounds = ref<CSSProperties>({})
const wrapper = ref<HTMLElement | null>(null)
const skeleton = ref<ComponentPublicInstance | null>(null)
let clickedPosition: DOMRect | undefined
let clickedRoadmapGoal: HTMLElement | undefined
let origin:
  | {
      path: string
      position: DOMRect
      goal?: HTMLElement
    }
  | undefined
let pendingRoute: RouteLocationNormalized | undefined
let animation: ReturnType<typeof storyOpeningAnimation> | undefined
let finishOpening: (() => void) | undefined
let stopWaitingForMilestone: WatchStopHandle | undefined
const roadmapRouteNames = ["roadmap", "past-goals"]

const isRoadmapRoute = (route: RouteLocationNormalized) =>
  roadmapRouteNames.includes(route.name as string)

function cleanup() {
  stopWaitingForMilestone?.()
  stopWaitingForMilestone = undefined
  animation?.kill()
  animation = undefined
  finishOpening?.()
  finishOpening = undefined
  pendingRoute = undefined
  visible.value = false
}

function captureOrigin(event: MouseEvent) {
  if (!isRoadmapRoute(router.currentRoute.value)) return
  clickedPosition = undefined
  clickedRoadmapGoal = undefined
  if (!(event.target instanceof Element)) return

  const control = event.target.closest<HTMLElement>("[data-milestone-route-transition]")
  if (!control) return

  clickedPosition = control.getBoundingClientRect()
  clickedRoadmapGoal = event.target.closest<HTMLElement>("[data-roadmap-goal]") ?? undefined
}

const removeBeforeResolve = router.beforeResolve(async (to, from) => {
  const position = clickedPosition
  const roadmapGoal = clickedRoadmapGoal
  clickedPosition = undefined
  clickedRoadmapGoal = undefined
  if (to.name === from.name) return

  cleanup()
  const opening = to.name === "Milestones" && isRoadmapRoute(from) && position !== undefined
  const closing = from.name === "Milestones" && isRoadmapRoute(to) && origin?.path === to.fullPath
  if (!opening && !closing) {
    origin = undefined
    return
  }

  const content = document.getElementById("mainContent")
  if (!content) return
  const rect = content.getBoundingClientRect()
  if (opening && position) {
    origin = {
      path: from.fullPath,
      position,
      goal: roadmapGoal
    }
  }
  const itemPosition = origin?.position
  pendingRoute = to
  bounds.value = {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`
  }
  visible.value = true
  await nextTick()
  if (pendingRoute !== to || !wrapper.value || !skeleton.value) return
  animation = storyOpeningAnimation({
    itemPosition,
    elementWrapper: wrapper.value,
    animateElement: skeleton.value.$el as HTMLElement
  })
  // Keep the live source underneath until the skeleton has unfolded.
  if (opening) {
    /**
     * play() only renders on the next animation frame, so the browser gets a
     * chance to paint the skeleton at full size first — that was the flicker.
     * The offset must be above zero: at exactly 0 the first tween has not
     * started yet (immediateRender is false) and applies no values.
     */
    animation.progress(0.0001)
    await new Promise<void>(resolve => {
      finishOpening = resolve
      animation?.eventCallback("onComplete", resolve).play()
    })
  } else {
    animation.progress(1)
  }
})

const removeAfterEach = router.afterEach(async (to, _from, failure) => {
  if (pendingRoute !== to) return
  if (failure) {
    cleanup()
    return
  }
  await nextTick()
  if (pendingRoute !== to) return
  if (to.name === "Milestones") {
    // Keep one skeleton visible until the destination replaces its loading state.
    stopWaitingForMilestone = watch(
      () => wait.is("get.milestones"),
      async isLoading => {
        if (isLoading) return

        await nextTick()
        if (pendingRoute === to && !wait.is("get.milestones")) cleanup()
      },
      { immediate: true, flush: "post" }
    )
  }
  // Fold over the destination page after its route has mounted.
  else {
    const roadmapOrigin = origin?.path === to.fullPath ? origin : undefined
    const goal = roadmapOrigin?.goal?.isConnected ? roadmapOrigin.goal : undefined
    animation
      ?.eventCallback("onReverseComplete", () => {
        cleanup()
        origin = undefined
        if (goal?.isConnected) storyScalingAnimation(goal).play()
      })
      .reverse(0)
  }
})

const removeErrorHandler = router.onError(cleanup)
document.addEventListener("click", captureOrigin, true)
window.addEventListener("resize", cleanup)
onBeforeUnmount(() => {
  removeBeforeResolve()
  removeAfterEach()
  removeErrorHandler()
  document.removeEventListener("click", captureOrigin, true)
  window.removeEventListener("resize", cleanup)
  cleanup()
  origin = undefined
})
</script>
